/**
 * Quick-add (pane / node context menus) and related actions: open menus at cursor,
 * copy node id, delete node, place entity or file at flow position.
 */

import { markInsertSource } from "@/pages/canvas/analytics/insertSource";
import { buildEntityNodeAtFlowPosition } from "@/pages/canvas/utils/canvasEntityNodes";
import type { Entity } from "@cohesive/ui-components";
import type { Node } from "@xyflow/react";
import { useReactFlow } from "@xyflow/react";
import { useCallback } from "react";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { snapFlowPosition } from "../utils/quickAddCursor";
import {
  attachNodeToSectionIfApplicable,
  buildDetachUpdatesForSectionRemovals,
} from "../utils/sectionMembership";
import { useCanvasNodePlacement } from "./useCanvasNodePlacement";
import { useCanvasPermissions } from "./useCanvasPermissions";

export type {
  NodeContextMenuState,
  QuickAddCursorState,
} from "../context/CanvasUiContext";

export interface UseCanvasQuickAddHandlersParams {
  isPlacingTextNote: boolean;
  isPlacingDoc: boolean;
  isPlacingDeferredQuickCompose?: boolean;
  copyNodeIds: (ids: string[]) => void;
  duplicateNodeIds: (ids: string[]) => void;
  /** Right-click quick add — not the explorer/sidebar entry point. */
  onQuickAddOpenedFromPane?: () => void;
}

export { buildQuickAddCursorFromClientPos } from "../utils/quickAddCursor";

export function useCanvasQuickAddHandlers({
  isPlacingTextNote,
  isPlacingDoc,
  isPlacingDeferredQuickCompose = false,
  copyNodeIds,
  duplicateNodeIds,
  onQuickAddOpenedFromPane,
}: UseCanvasQuickAddHandlersParams) {
  const { canEdit } = useCanvasPermissions();
  const reactFlowInstance = useReactFlow<Node>();
  const {
    reactFlowWrapperRef,
    setNodeContextMenu,
    setQuickAddCursor,
    setShowQuickAdd,
  } = useCanvasUi();
  const { getEntityNodeWidth, getEntityNodeHeight } = useCanvasNodePlacement();
  const { removeNodes, addNode, batchUpdateNodeParents } = useCanvas();
  const handlePaneContextMenu = useCallback(
    (e: React.MouseEvent | MouseEvent) => {
      e.preventDefault();
      if (!canEdit) return;
      if (isPlacingTextNote || isPlacingDoc || isPlacingDeferredQuickCompose)
        return;

      const wrapperRect = reactFlowWrapperRef.current?.getBoundingClientRect();
      if (!wrapperRect) return;

      setNodeContextMenu(null);
      onQuickAddOpenedFromPane?.();

      setQuickAddCursor({
        screenX: e.clientX - wrapperRect.left,
        screenY: e.clientY - wrapperRect.top,
        clientX: e.clientX,
        clientY: e.clientY,
        flowPosition: snapFlowPosition(
          reactFlowInstance.screenToFlowPosition({
            x: e.clientX,
            y: e.clientY,
          }),
        ),
      });
      setShowQuickAdd(true);
    },
    [
      canEdit,
      isPlacingTextNote,
      isPlacingDoc,
      isPlacingDeferredQuickCompose,
      reactFlowWrapperRef,
      reactFlowInstance,
      onQuickAddOpenedFromPane,
      setNodeContextMenu,
      setQuickAddCursor,
      setShowQuickAdd,
    ],
  );

  /**
   * Open the node context menu at a client position. Shared by the React Flow
   * `onNodeContextMenu` path (kept for the keyboard context-menu key) and the
   * right-click pan gesture (useNodeRightClickPan), which defers the menu to
   * an unmoved pointerup — mirroring how the pane defers quick-add so a
   * right-drag pans instead.
   */
  const openNodeContextMenuAt = useCallback(
    (nodeId: string, clientX: number, clientY: number) => {
      if (!canEdit) return;
      if (isPlacingTextNote || isPlacingDoc || isPlacingDeferredQuickCompose)
        return;

      const wrapperRect = reactFlowWrapperRef.current?.getBoundingClientRect();
      if (!wrapperRect) return;

      setShowQuickAdd(false);
      setQuickAddCursor(null);

      setNodeContextMenu({
        nodeId,
        nodeType: reactFlowInstance.getNode(nodeId)?.type,
        screenX: clientX - wrapperRect.left,
        screenY: clientY - wrapperRect.top,
        clientX,
        clientY,
        flowPosition: reactFlowInstance.screenToFlowPosition({
          x: clientX,
          y: clientY,
        }),
        origin: "pointer",
      });
    },
    [
      canEdit,
      isPlacingTextNote,
      isPlacingDoc,
      isPlacingDeferredQuickCompose,
      reactFlowWrapperRef,
      reactFlowInstance,
      setShowQuickAdd,
      setQuickAddCursor,
      setNodeContextMenu,
    ],
  );

  const handleNodeContextMenu = useCallback(
    (e: React.MouseEvent, node: Node) => {
      if (!canEdit) return;
      e.preventDefault();
      openNodeContextMenuAt(node.id, e.clientX, e.clientY);
    },
    [canEdit, openNodeContextMenuAt],
  );

  const handleNodeContextMenuCopy = useCallback(
    (nodeId: string) => {
      copyNodeIds([nodeId]);
    },
    [copyNodeIds],
  );

  const handleNodeContextMenuDuplicate = useCallback(
    (nodeId: string) => {
      if (!canEdit) return;
      duplicateNodeIds([nodeId]);
    },
    [canEdit, duplicateNodeIds],
  );

  // Section-only: remove the section wrapper but detach (keep) its children.
  const handleNodeContextMenuDeleteWrapperOnly = useCallback(
    (nodeId: string) => {
      if (!canEdit) return;
      const allNodes = reactFlowInstance.getNodes();
      const detach = buildDetachUpdatesForSectionRemovals(
        [nodeId],
        allNodes,
        reactFlowInstance.getInternalNode,
      );
      if (detach.length > 0) {
        batchUpdateNodeParents(detach);
      }
      removeNodes([nodeId]);
    },
    [canEdit, reactFlowInstance, batchUpdateNodeParents, removeNodes],
  );

  const handleNodeContextMenuDelete = useCallback(
    (nodeId: string) => {
      if (!canEdit) return;
      const allNodes = reactFlowInstance.getNodes();
      const target = allNodes.find((n) => n.id === nodeId);
      if (!target) return;

      const detach = buildDetachUpdatesForSectionRemovals(
        [nodeId],
        allNodes,
        reactFlowInstance.getInternalNode,
      );
      if (detach.length > 0) {
        batchUpdateNodeParents(detach);
      }
      removeNodes([nodeId]);
    },
    [canEdit, reactFlowInstance, batchUpdateNodeParents, removeNodes],
  );

  const handleQuickAddSelectEntity = useCallback(
    (entity: Entity, flowPosition: { x: number; y: number }) => {
      const newNode = buildEntityNodeAtFlowPosition(entity, flowPosition, {
        getEntityNodeWidth,
        getEntityNodeHeight,
      });
      const attached = attachNodeToSectionIfApplicable({
        node: newNode,
        nodes: reactFlowInstance.getNodes(),
        getInternalNode: (id) => reactFlowInstance.getInternalNode(id),
      });
      markInsertSource("quick_add");
      addNode(attached);
    },
    [addNode, reactFlowInstance, getEntityNodeWidth, getEntityNodeHeight],
  );

  return {
    handlePaneContextMenu,
    handleNodeContextMenu,
    openNodeContextMenuAt,
    handleNodeContextMenuCopy,
    handleNodeContextMenuDuplicate,
    handleNodeContextMenuDeleteWrapperOnly,
    handleNodeContextMenuDelete,
    handleQuickAddSelectEntity,
  };
}
